import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Camera, Image as ImageIcon, ImagePlus, Loader2, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MyMediaPickerDialog } from '@/components/media/MyMediaPickerDialog';
import { ProFeatureDialog } from '@/components/ProFeatureDialog';
import { ProBadge } from '@/components/ProBadge';
import { AiCoverDialog } from '@/components/cover/AiCoverDialog';
import { CoverFrameChooserDialog } from '@/components/cover/CoverFrameChooserDialog';
import { settleFrameCandidate } from '@/hooks/use-cover-frame-candidates';
import { dataUrlsLookIdentical } from '@/lib/cover/dataUrlsLookIdentical';
import type { FrameCandidates } from '@/lib/cover/resolveFrameSourceSelection';
import type { CoverFrameSource } from '@/lib/cover/buildPreviewStillInputProps';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useRegisterUserMedia } from '@/hooks/use-register-user-media';
import { useCoverFrameCapture } from '@/hooks/use-cover-frame-capture';
import type { CoverCropFraming } from '@/lib/cover/resolveCropFramingAtTime';
import {
  buildFrameCover,
  buildUploadCover,
  sanitizeCoverImage,
  type ClipCoverImage,
} from '@/lib/cover/coverImage';
import { formatFrameTime } from '@/lib/timeline/exportFrameTiming';
import { aiCoverDimensions, buildAiCoverMediaName } from '@/lib/cover/aiCoverMedia';

export type CoverImageControlProps = {
  cover: ClipCoverImage | null;
  onChange: (cover: ClipCoverImage | null) => void;
  /** Full-quality source video used for the frame capture. */
  videoUrl: string | undefined;
  /** Source-video playhead, in seconds. */
  currentTime: number;
  /** width/height of the export (9/16 or 16/9), for the preview box and
   *  the captured frame's crop. */
  exportAspect: number;
  /** The preview's reframe (center as 0..1 of the source, and zoom) at a
   *  source time, or null for centered. A captured frame is cropped to
   *  `exportAspect` around it so the cover matches what the preview shows. */
  resolveCropFraming?: (sourceTime: number) => CoverCropFraming | null;
  /** Renders the frame the editor preview currently shows (layout,
   *  subtitles, layers) as a data URL. Offered next to the raw video frame;
   *  omitted ⇒ only the video frame is available. */
  capturePreviewFrame?: () => Promise<string | null>;
  disabled?: boolean;
  /** Whether the viewer has PRO features. Without it, "Gerar com IA" stays
   *  clickable and opens the PRO upgrade dialog instead. */
  hasProAccess: boolean;
  /** The saveable clip id the AI generation is attached to. */
  clipId: string | null;
  /** Current clip title, used to pre-fill the AI prompt. */
  clipTitle: string | null;
};

type BusyState = 'capturing' | 'uploading' | null;
type CoverError = 'captureError' | 'uploadError' | 'invalidImage' | null;

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg';

/** "Capa do vídeo": the per-clip image prepended as the export's first frame.
 *  A compact trigger for the timeline toolbar (thumbnail of the current cover)
 *  that opens the cover panel in a popover.
 *
 *  All state (busy capture/upload, errors, the picker and AI dialogs, the file
 *  input) lives in this always-mounted component, not in the popover content,
 *  so closing the popover mid-capture or while a dialog is open drops nothing.
 *  Mount it keyed by clip so an in-flight capture/upload can never land on a
 *  clip the user has since switched to (results after unmount are dropped). */
export function CoverImageControl({
  cover,
  onChange,
  videoUrl,
  currentTime,
  exportAspect,
  resolveCropFraming,
  capturePreviewFrame,
  disabled = false,
  hasProAccess,
  clipId,
  clipTitle,
}: CoverImageControlProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUploadUrl } = useUploadFile();
  const registerUserMedia = useRegisterUserMedia();
  const { captureFramedDataUrl, uploadFramedDataUrl } = useCoverFrameCapture({
    videoUrl,
    exportAspect,
    resolveCropFraming,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const [busy, setBusy] = useState<BusyState>(null);
  const [error, setError] = useState<CoverError>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [proGateOpen, setProGateOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // Set when both candidates differ: the side-by-side chooser, for the
  // playhead at click time.
  const [chooser, setChooser] = useState<{ time: number; candidates: FrameCandidates } | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const actionsDisabled = disabled || busy !== null;

  const commit = (next: ClipCoverImage | null) => {
    setReplacing(false);
    onChange(next);
  };

  // Every picked/uploaded/captured cover goes through the same sanitizer the
  // saved data does, so a URL that would be silently dropped on save (e.g. a
  // non-https upload) is rejected here with an inline error instead, and the
  // previous cover stays.
  const commitCandidate = (candidate: ClipCoverImage): boolean => {
    const next = sanitizeCoverImage(candidate);
    if (!next) {
      setError('invalidImage');
      return false;
    }
    commit(next);
    return true;
  };

  const loadVideoFrame = useCallback(
    async (time: number): Promise<string | null> => (await captureFramedDataUrl(time))?.dataUrl ?? null,
    [captureFramedDataUrl],
  );

  // "Usar frame atual": render both candidates for the playhead at click
  // time. When they look the same (nothing drawn over the video at that
  // moment) the frame is used directly; otherwise the user picks one.
  const captureCurrentFrame = async () => {
    if (actionsDisabled || !videoUrl) return;
    const time = currentTime;
    setError(null);
    setBusy('capturing');
    try {
      const [preview, video] = await Promise.all([
        settleFrameCandidate('preview', capturePreviewFrame),
        settleFrameCandidate('video', () => loadVideoFrame(time)),
      ]);
      if (!mountedRef.current) return;
      if (preview.status !== 'ready' && video.status !== 'ready') {
        setError('captureError');
        return;
      }
      if (
        preview.status === 'ready' &&
        video.status === 'ready' &&
        (await dataUrlsLookIdentical(preview.dataUrl, video.dataUrl, exportAspect))
      ) {
        if (!mountedRef.current) return;
        await handleChosenFrame(video.dataUrl, time);
        return;
      }
      if (!mountedRef.current) return;
      setChooser({ time, candidates: { preview, video } });
    } finally {
      if (mountedRef.current) setBusy((current) => (current === 'capturing' ? null : current));
    }
  };

  const retryChooserCandidate = async (source: CoverFrameSource) => {
    const current = chooser;
    if (!current) return;
    const loader = source === 'preview' ? capturePreviewFrame : () => loadVideoFrame(current.time);
    setChooser((prev) =>
      prev && prev.time === current.time
        ? { ...prev, candidates: { ...prev.candidates, [source]: { status: 'loading' } } }
        : prev,
    );
    const candidate = await settleFrameCandidate(source, loader);
    if (!mountedRef.current) return;
    setChooser((prev) =>
      prev && prev.time === current.time
        ? { ...prev, candidates: { ...prev.candidates, [source]: candidate } }
        : prev,
    );
  };

  // The modal chooser takes focus, which dismisses the Capa popover; bring
  // the panel back so the upload progress, result or error is visible.
  const closeFrameChooser = () => {
    setChooser(null);
    setPanelOpen(true);
  };

  const handleChosenFrame = async (dataUrl: string, captureTime: number) => {
    closeFrameChooser();
    setError(null);
    setBusy('uploading');
    try {
      const result = await uploadFramedDataUrl(dataUrl, captureTime, {
        isActive: () => mountedRef.current,
      });
      if (!mountedRef.current) return;
      if (result.ok === false) {
        if (result.error !== 'cancelled') setError(result.error);
        return;
      }
      // Rejected before registering: an unusable URL must not add a My Media row.
      const next = sanitizeCoverImage(buildFrameCover(result.url, result.captureTime));
      if (!next) {
        setError('invalidImage');
        return;
      }
      registerUserMedia({ url: result.url, kind: 'image', name: result.fileName });
      commit(next);
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  // The AI reference frame: the same candidates and upload path as "Usar
  // frame atual", but never registered in My Media and never committed as a
  // cover.
  const uploadReference = useCallback(
    async (dataUrl: string, captureTime: number): Promise<string | null> => {
      const result = await uploadFramedDataUrl(dataUrl, captureTime, { isActive: () => mountedRef.current });
      return result.ok ? result.url : null;
    },
    [uploadFramedDataUrl],
  );

  const handleAiClick = () => {
    if (!hasProAccess) {
      setProGateOpen(true);
      return;
    }
    setAiOpen(true);
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again re-fires change.
    event.target.value = '';
    if (!file || actionsDisabled) return;
    setError(null);
    setBusy('uploading');
    try {
      const url = await getUploadUrl(file);
      if (!mountedRef.current) return;
      if (!url) {
        setError('uploadError');
        return;
      }
      // Rejected before registering: an unusable URL must not add a My Media row.
      const next = sanitizeCoverImage(buildUploadCover(url));
      if (!next) {
        setError('invalidImage');
        return;
      }
      registerUserMedia({ url, kind: 'image', name: file.name });
      commit(next);
    } catch {
      if (mountedRef.current) setError('uploadError');
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  const sourceButtons = (
    <div className="grid grid-cols-2 gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-auto min-h-8 whitespace-normal px-2 py-1.5 text-xs leading-tight"
        disabled={actionsDisabled || !videoUrl}
        onClick={() => void captureCurrentFrame()}
      >
        <Camera className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('editor.coverImage.captureFrame')}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-auto min-h-8 whitespace-normal px-2 py-1.5 text-xs leading-tight"
        disabled={actionsDisabled}
        onClick={() => setPickerOpen(true)}
      >
        <ImagePlus className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {t('editor.coverImage.chooseImage')}
      </Button>
    </div>
  );

  // Never disabled for free users: the PRO gate explains itself at click time.
  const aiButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-auto min-h-8 w-full whitespace-normal px-2 py-1.5 text-xs leading-tight"
      disabled={actionsDisabled}
      onClick={handleAiClick}
    >
      <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {t('editor.coverImage.ai.button')}
      <ProBadge className="ml-1.5 px-1.5 py-0 text-[10px]" />
    </Button>
  );

  const busyLabel = busy
    ? t(busy === 'capturing' ? 'editor.coverImage.capturing' : 'editor.coverImage.uploading')
    : null;

  return (
    <>
      <Popover open={panelOpen} onOpenChange={setPanelOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 gap-2 pl-1 pr-3 text-sm font-semibold"
            data-testid="cover-image-trigger"
            title={t('editor.coverImage.title')}
            aria-label={t('editor.coverImage.title')}
          >
            {/* Square thumbnail: a 9:16 sliver was too thin to read at
                toolbar size, so the cover is cropped to fill it. */}
            <span
              className="relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted ring-1 ring-border"
              data-testid="cover-image-trigger-thumb"
              aria-hidden="true"
            >
              {cover ? (
                <img src={cover.url} alt="" className="h-full w-full object-cover" />
              ) : (
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              )}
              {busy && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
                </span>
              )}
            </span>
            <span>{t('editor.coverImage.badge')}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 max-w-[calc(100vw-2rem)] space-y-3 p-4" data-testid="cover-image-panel">
          <div className="space-y-1">
            <p className="text-sm font-semibold">{t('editor.coverImage.title')}</p>
            <p className="text-xs leading-snug text-muted-foreground">{t('editor.coverImage.hint')}</p>
          </div>
          {cover ? (
            <>
              <div className="flex justify-center rounded-md bg-muted/40 p-2">
                <div
                  data-testid="cover-image-preview"
                  className="relative w-full max-w-[140px] overflow-hidden rounded-md border border-border bg-black"
                  style={{ aspectRatio: String(exportAspect) }}
                >
                  <img
                    src={cover.url}
                    alt={t('editor.coverImage.previewAlt')}
                    className="h-full w-full object-cover"
                  />
                  {busy && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden="true" />
                    </div>
                  )}
                </div>
              </div>
              {cover.source === 'frame' && typeof cover.sourceTime === 'number' && (
                <p className="text-xs text-muted-foreground">
                  {t('editor.coverImage.frameAt', { time: formatFrameTime(cover.sourceTime) })}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={replacing ? 'secondary' : 'outline'}
                  size="sm"
                  className="h-auto min-h-8 whitespace-normal px-2 py-1.5 text-xs leading-tight"
                  aria-expanded={replacing}
                  disabled={actionsDisabled}
                  onClick={() => setReplacing((open) => !open)}
                >
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t('editor.coverImage.replace')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-auto min-h-8 whitespace-normal px-2 py-1.5 text-xs leading-tight"
                  disabled={actionsDisabled}
                  onClick={() => {
                    setError(null);
                    commit(null);
                  }}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t('editor.coverImage.remove')}
                </Button>
              </div>
              {replacing && sourceButtons}
              {aiButton}
            </>
          ) : (
            <>
              {sourceButtons}
              {aiButton}
              {busyLabel && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {busyLabel}
                </p>
              )}
            </>
          )}
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {t(`editor.coverImage.${error}`)}
            </p>
          )}
        </PopoverContent>
      </Popover>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        data-testid="cover-image-file-input"
        disabled={actionsDisabled}
        onChange={(event) => void handleFile(event)}
      />
      {chooser && (
        <CoverFrameChooserDialog
          open
          onOpenChange={(open) => {
            if (!open) closeFrameChooser();
          }}
          exportAspect={exportAspect}
          candidates={chooser.candidates}
          onRetry={(source) => void retryChooserCandidate(source)}
          onConfirm={({ dataUrl }) => void handleChosenFrame(dataUrl, chooser.time)}
        />
      )}
      <MyMediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(item) => {
          // Reusing an existing My Media image: no new user_medias row.
          setPickerOpen(false);
          setError(null);
          commitCandidate(buildUploadCover(item.url));
        }}
        onUploadInstead={() => {
          setPickerOpen(false);
          fileInputRef.current?.click();
        }}
      />
      <ProFeatureDialog
        open={proGateOpen}
        onOpenChange={setProGateOpen}
        onUpgrade={() => navigate('/plans')}
        title={t('editor.coverImage.ai.proDialog.title')}
        body={t('editor.coverImage.ai.proDialog.body')}
        dismissLabel={t('editor.coverImage.ai.proDialog.dismiss')}
        upgradeLabel={t('editor.coverImage.ai.proDialog.upgrade')}
        testIdPrefix="ai-cover-pro"
      />
      {/* Mounted only while open: the generation hook's polling lives and
          dies with the dialog, and a closed card makes no quota request. */}
      {aiOpen && (
        <AiCoverDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          clipId={clipId}
          clipTitle={clipTitle}
          exportAspect={exportAspect}
          referenceAvailable={Boolean(videoUrl)}
          loadPreviewFrame={capturePreviewFrame}
          loadVideoFrame={loadVideoFrame}
          uploadReference={uploadReference}
          currentTime={currentTime}
          onUseCover={(url) => {
            setError(null);
            if (!commitCandidate(buildUploadCover(url))) return;
            // Backstop for the backend listener's registration (deduped by
            // url): a missed listener must not leave the cover unprotected
            // from the storage reaper. The AI reference frame is never
            // registered.
            registerUserMedia({
              url,
              kind: 'image',
              name: buildAiCoverMediaName(new Date()),
              ...aiCoverDimensions(exportAspect),
            });
          }}
        />
      )}
    </>
  );
}
